// The page behind the "I object" link in a nominee designation notice (Wave C item C2,
// gap-analysis finding F4).
//
// UNAUTHENTICATED BY DESIGN, and the whole reason this is a page rather than a control
// inside the app. The person reading it has no account with us, never agreed to anything,
// and is here to tell us to stop holding their details. Making them sign up first - or
// wait for a one-time password sent to the very address they are objecting to us holding -
// would charge a fee in friction for a right that is meant to be free. The token in their
// email is the whole credential, exactly as /confirm-active works for an account holder.
//
// IT DOES NOT ACT ON ARRIVAL, which is where it deliberately differs from ConfirmActivePage.
// Confirming activity is what the owner already meant to do, so doing it on load is a
// convenience. Objecting is irreversible and is somebody's decision about their own data;
// an email client that pre-fetches links, or a mistaken click, must not make it for them.
// So the page states what will happen and waits for a deliberate press.
//
// The path is /nominee-objection, not /nominee/..., and that is load-bearing: nginx proxies
// ^/nominee/ to Express, so a client route under that prefix would be answered with JSON
// (see api-specification.md - no client route may share an API path, learned the hard way
// twice in this repo).
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, LoaderPinwheel, ShieldOff, XCircle } from 'lucide-react';
import Header from '../components/Header';
import Button from '../components/elements/Button';
import { objectToNomination } from '../utils/nomineeApi';

export default function NomineeObjectionPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || '';
  const navigate = useNavigate();

  // 'ready' (waiting for a deliberate press), 'submitting', 'done', 'error'.
  const [status, setStatus] = useState(token && id ? 'ready' : 'error');
  const [message, setMessage] = useState(
    token && id ? '' : 'This objection link is incomplete. Please use the full link from the email we sent you.'
  );

  useEffect(() => {
    document.title = 'Object to a Nomination | Eternal Locker™';
  }, []);

  const submit = async () => {
    setStatus('submitting');
    try {
      const res = await objectToNomination(id, token);
      if (res.success) {
        setStatus('done');
        setMessage(res.message || 'Your objection has been recorded.');
      } else {
        setStatus('error');
        setMessage(res.message || 'This objection link is not valid.');
      }
    } catch {
      setStatus('error');
      setMessage('Something went wrong recording your objection. Please try again, or email privacy@eternallocker.com.');
    }
  };

  return (
    <>
      <Header type="large" />
      <div className="min-h-screen bg-background-light flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-card-background rounded-xl shadow-2xl p-8 md:p-12 border border-accent-main">
          {status === 'ready' && (
            <div className="space-y-6">
              <div className="text-center">
                <ShieldOff className="w-20 h-20 text-accent-main mx-auto mb-6" />
                <h1 className="text-3xl font-bold text-primary-dark mb-4">Object to this nomination</h1>
                <p className="text-lg text-text-muted">
                  Someone has named you as a nominee on their Eternal Locker™ account. If you
                  do not want us holding your details, you can say so here.
                </p>
              </div>

              {/* Consequences before the press, not after it. This is irreversible from the
                  objector's side, and the one thing they might not expect - that the person
                  who named them is told - has to be said before they decide, not discovered
                  afterwards. */}
              <div className="bg-primary-light border border-accent-main rounded-lg p-6">
                <h2 className="text-lg font-semibold text-primary-dark mb-3">What happens when you object:</h2>
                <ul className="text-primary-dark text-sm space-y-2 list-disc list-inside">
                  <li>We stop holding your name, relationship and contact number. Only your email address is kept, so that this nomination cannot simply be re-created.</li>
                  <li>You will never be contacted about this account again, and you will not be given access to it, whatever happens to the account holder.</li>
                  <li><strong>The person who named you will be told that you declined.</strong></li>
                  <li>This cannot be undone from here. If you change your mind, they would have to name you again.</li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={submit}>I object to being a nominee</Button>
                <Button type="secondary" onClick={() => navigate('/')}>Leave this as it is</Button>
              </div>

              <p className="text-xs text-text-muted text-center">
                Questions? Email privacy@eternallocker.com. You may also complain to the Data
                Protection Board of India.
              </p>
            </div>
          )}

          {status === 'submitting' && (
            <div className="text-center">
              <LoaderPinwheel className="w-20 h-20 text-accent-main mx-auto mb-6 animate-spin" />
              <h1 className="text-3xl font-bold text-primary-dark mb-4">Recording your objection…</h1>
            </div>
          )}

          {status === 'done' && (
            <div className="text-center">
              <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-primary-dark mb-4">Your objection has been recorded</h1>
              <p className="text-lg text-text-muted mb-8">{message}</p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8 text-left">
                <p className="text-green-800 text-sm m-0">
                  Nothing further is needed from you. We have told the account holder, and we
                  will not contact you about their locker again. If you would like a record of
                  what we held about you, or believe this was in error, email
                  privacy@eternallocker.com.
                </p>
              </div>
              <Button type="secondary" onClick={() => navigate('/')}>Go to Eternal Locker</Button>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <XCircle className="w-20 h-20 text-red-600 mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-primary-dark mb-4">We could not use this link</h1>
              <p className="text-lg text-text-muted mb-8">{message}</p>
              {/* No diagnosis offered, because the server deliberately does not give one:
                  telling apart "no such nomination" from "wrong token" would make this an
                  oracle for whether a given record is a nomination, which links two people
                  and implies a death or incapacity plan. So this offers a route to a human
                  instead of a guess. */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8 text-left">
                <p className="text-red-800 text-sm m-0">
                  Please open the link directly from the email we sent you, rather than typing
                  or shortening it. If it still does not work, email
                  privacy@eternallocker.com and we will handle your objection ourselves — it
                  does not depend on this page working.
                </p>
              </div>
              <Button type="secondary" onClick={() => navigate('/contact')}>Contact us</Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
